import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import moment from 'moment';

import {getBasket, reOrderBasket} from '../../actions/basket.action';
import {setSetting} from '../../actions/settings.action';
import {resetError} from '../../actions/error.action';
import {
  getOrderHistory,
  getCanceledOrders,
  getActiveOrders,
  cancelOrder,
  trackOrder,
  resetTrackOrder,
  reAuthOrderPayment,
} from '../../actions/order.action';
import {toggleSwitchAccount} from '../../actions/modal.action';
import {
  startDownloadingPdf,
  finishDownloadingPdf,
} from '../../actions/loading.action';
import {
  Wrapper,
  Head,
  Title,
  SubTitle,
  Content,
  ButtonWrapper,
  UserInfo,
} from '../../styles/pages/pages';
import {
  SubTitleWrapper,
  Row,
  Container,
  OrderLinkButton,
  Error,
  PaginationWrapper,
  TabsWrapper,
  Tab,
} from './OrdersPage.style';
import Order from './Order';
import {ExtraWrapper} from '../../styles/components/wrapper';
import Loading from '../../components/Loading/Loading';
import PopupModal from '../../components/PopupModal/PopupModal';
import ReOrderBasketModal from '../../components/PopupModal/ReOrderBasketModal';
import {
  AX_BASKET_ERRORS,
  CONTENT_SITE,
  ROUTES,
  ORDER_EDIT_TIMEOUT,
} from '../../constants/constants';
import UnauthInfoPanel from '../../components/UnauthInfoPanel/UnauthInfoPanel';
import {editOrder, setCurrentRoute} from '../../actions';
import EditOrderBasketModal from '../../components/PopupModal/EditOrderBasketModal';
import Invoice from './Invoice';
import InvoicesPDFModal from '../../components/PopupModal/InvoicesPDFModal';
import Pagination from '../../components/Pagination/Pagination';
import {resetNextRoute} from '../../actions/nextRoute.action';
import OffersModal from '../../components/PopupModal/OffersModal';
import {setPrevRoute} from '../../actions/prevRoute.action';
import {getInvoiceApiUrl} from '../../helpers/string.helper';

const TABS = {
  ACTIVE: 'active',
  PREVIOUS: 'previous',
  CANCELLED: 'cancelled',
};

class OrdersPage extends PureComponent {
  static propTypes = {
    basket: PropTypes.object,
  };
  state = {
    reOrdering: false,
    editing: false,
    showReOrderWarningModal: false,
    showEditOrderWarningModal: false,
    showTrackingErrorModal: false,
    orderId: null,
    showNewOfferModal: null,
    downloadErrorIndex: -1,
    downloadingStatus: {},
    activeTab: TABS.ACTIVE,
  };
  settingsUpdated = false;
  prevBasketItems = [];

  setActiveTab = tab => this.setState({activeTab: tab});

  componentDidMount() {
    const {
      getOrderHistory,
      getCanceledOrders,
      getActiveOrders,
      auth,
      setCurrentRoute,
      setPrevRoute,
    } = this.props;
    setCurrentRoute(ROUTES.ORDERS);
    setPrevRoute(ROUTES.ORDERS);
    if (auth && auth.c_account) {
      getOrderHistory(auth.c_account);
      getCanceledOrders(auth.c_account);
      getActiveOrders(auth.c_account);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      getOrderHistory,
      getCanceledOrders,
      getActiveOrders,
      auth,
      history,
      loading,
      basket,
      errors,
      cancellingOrder,
      nextRoute,
      resetNextRoute,
    } = this.props;
    if (
      (prevProps.auth !== auth && auth && auth.c_account) ||
      (prevProps.loading.cancellingOrder && !loading.cancellingOrder)
    ) {
      getOrderHistory(auth.c_account);
      getCanceledOrders(auth.c_account);
      getActiveOrders(auth.c_account);
    }
    if (nextRoute) {
      resetNextRoute();
      history.push({
        pathname: nextRoute,
        state: {
          isReAuth: true,
        },
      });
    }
    if (errors && errors.reOrderServerError) {
      this.setState({reOrdering: false, showReOrderWarningModal: false});
    }
    if (errors && errors.editOrderServerError) {
      this.setState({editing: false, showEditOrderWarningModal: false});
    }
    if (errors && errors.trackOrder) {
      this.setState({showTrackingErrorModal: true});
    }
    if (
      errors &&
      (errors.basketAlreadyPicked ||
        errors.axBasketNotFound ||
        errors.basketNotEditable)
    ) {
      this.setState({editing: false});
    }
    if (
      basket &&
      basket.uuid &&
      (this.state.reOrdering || this.state.editing)
    ) {
      // re-ordering or edit order started, but settings not yet updated, so we update settings
      if (!this.settingsUpdated && prevProps.basket) {
        this.settingsUpdated = true;
        this.prevBasketItems = prevProps.basket.items;
      }
      // once settings and basket updated, we redirect user to basket page to show the re-ordered/edited basket
      if (
        this.settingsUpdated &&
        prevProps.basket &&
        prevProps.basket.uuid === basket.uuid
      ) {
        history.push('/basket');
      }
    }
    if (
      prevProps.cancellingOrder &&
      !cancellingOrder &&
      !errors.cancelOrderServerError &&
      auth &&
      auth.c_account
    ) {
      getOrderHistory(auth.c_account);
    }
    if (
      !this.cutOffExpiryTimer &&
      this.state.showEditOrderWarningModal &&
      this.state.cutOffDateTime
    ) {
      this.cutOffExpiryTimer = setInterval(
        this.checkCutOffExpiry,
        ORDER_EDIT_TIMEOUT
      );
    }
    if (
      this.cutOffExpiryTimer &&
      (!this.state.showEditOrderWarningModal || !this.state.cutOffDateTime)
    ) {
      clearInterval(this.cutOffExpiryTimer);
      this.cutOffExpiryTimer = null;
    }
  }

  componentWillUnmount() {
    if (this.cutOffExpiryTimer) {
      clearInterval(this.cutOffExpiryTimer);
    }
  }

  checkCutOffExpiry = () => {
    if (!this.state.cutOffDateTime || !this.state.showEditOrderWarningModal) {
      return;
    }
    const cutOffTime = moment(this.state.cutOffDateTime);
    const now = moment();
    if (now.isAfter(cutOffTime)) {
      clearInterval(this.cutOffExpiryTimer);
      window && window.location && window.location.reload();
    }
  };

  reOrder = withCurrentBasket => {
    // TODO use withCurrentBasket if "Add to the current basket" - when BE ready
    const {reOrderBasket, cleanBasket} = this.props;
    cleanBasket();
    reOrderBasket(this.state.orderId);
    this.setState({reOrdering: true});
  };

  cancelOrder = () => {
    const {cancelOrder} = this.props;
    cancelOrder(this.state.orderId);
    this.hideCancelOrderWarningModal();
  };

  editOrder = () => {
    const {editOrder, cleanBasket} = this.props;
    this.hideEditOrderWarningModal();
    cleanBasket();
    editOrder(this.state.orderId);
    this.setState({editing: true});
  };

  reOrderAddBasket = () => this.reOrder(true);

  hideReOrderWarningModal = () =>
    this.setState({showReOrderWarningModal: false});

  hideInvoicesModal = () =>
    this.setState({showInvoicesPDFModal: false, invoices: null});

  hideEditOrderWarningModal = () =>
    this.setState({showEditOrderWarningModal: false});

  showReOrderWarningModalFunc = orderId => {
    const {basket, modal} = this.props;
    if (
      this.state.showNewOfferModal === null &&
      modal.newOffers === undefined
    ) {
      this.setState({showNewOfferModal: true, orderId});
      return;
    }
    if (orderId) {
      this.setState({orderId});
    }
    if (basket) {
      this.setState({showReOrderWarningModal: true});
    } else {
      this.reOrder(this.state.orderId);
    }
  };

  viewNewOffer = () => {
    this.setState({showNewOfferModal: false});
    this.props.history.push(CONTENT_SITE.MIX_N_SAVE);
  };

  showEditOrderWarningModal = order => {
    this.setState(
      {orderId: order.orderId, cutOffDateTime: order.cutOffDateTime},
      () => {
        this.setState({showEditOrderWarningModal: true});
      }
    );
  };

  showCancelOrderWarningModal = orderId => {
    this.setState({orderId, showCancelOrderWarningModal: true});
  };

  hideCancelOrderWarningModal = () =>
    this.setState({showCancelOrderWarningModal: false});
  openSwitchAccountModal = () => this.props.toggleSwitchAccountModal(true);

  trackOrder = axBasketId => this.props.trackOrder(axBasketId);

  paginateInvoices = ({page}) => {
    const {getOrderHistory, auth} = this.props;
    getOrderHistory(auth.c_account, null, null, page);
  };

  paginateCancelledOrders = ({page}) => {
    const {getCanceledOrders, auth} = this.props;
    getCanceledOrders(auth.c_account, null, null, page);
  };

  paginateActiveOrders = ({page}) => {
    const {getActiveOrders, auth} = this.props;
    getActiveOrders(auth.c_account, null, null, page);
  };

  resetFailedToEdit = () => {
    const {resetError, errors, history} = this.props;
    resetError();
    if (
      errors.editOrderServerError === AX_BASKET_ERRORS.FAILED_TO_EDIT_ORDER ||
      errors.editOrderServerError === AX_BASKET_ERRORS.STARTED_PICKING
    ) {
      history.push('/');
    } else {
      window && window.location && window.location.reload();
    }
  };

  downloadPdf = (url, accessToken, index, invoiceId, isOnlyForDownload) => {
    const {startDownloading, finishDownloading} = this.props;
    let updatingState = {downloadErrorIndex: -1};
    if (invoiceId) {
      updatingState.downloadingStatus = Object.assign(
        {},
        this.state.downloadingStatus,
        {
          [invoiceId]: true,
        }
      );
    }
    this.setState(updatingState);

    const match = url.match(/\/invoice-pdf\/(S0\d+)/);
    const theInvoiceId = match && match[1] ? match[1] : 'invoice';
    const invoiceIndex = index > -1 ? index : -1;
    startDownloading();
    fetch(getInvoiceApiUrl(url), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/pdf',
      },
      body: JSON.stringify({theInvoiceId}),
    })
      .then(response => {
        if (!response.ok) {
          finishDownloading(invoiceIndex);
          let errorStatus = {downloadErrorIndex: invoiceIndex};
          if (invoiceId && this.state.downloadingStatus?.[invoiceId]) {
            errorStatus.downloadingStatus = Object.assign(
              {},
              this.state.downloadingStatus,
              {
                [invoiceId]: false,
              }
            );
          }
          this.setState(errorStatus);
          throw new Error('Failed to fetch PDF');
        }
        return response.blob();
      })
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        finishDownloading(invoiceIndex);
        if (invoiceId && this.state.downloadingStatus?.[invoiceId]) {
          const finishedStatus = Object.assign(
            {},
            this.state.downloadingStatus,
            {
              [invoiceId]: false,
            }
          );
          this.setState({downloadingStatus: finishedStatus});
        }
        if (!isOnlyForDownload) {
          window.open(blobUrl, '_blank');
          return;
        }
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = invoiceId + '.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
        finishDownloading(invoiceIndex);
        if (invoiceId && this.state.downloadingStatus?.[invoiceId]) {
          const finishedStatus = Object.assign(
            {},
            this.state.downloadingStatus,
            {
              [invoiceId]: false,
            }
          );
          this.setState({downloadingStatus: finishedStatus});
        }
      })
      .catch(error => {
        finishDownloading(invoiceIndex);
        let errorStatus = {downloadErrorIndex: invoiceIndex};
        if (invoiceId && this.state.downloadingStatus?.[invoiceId]) {
          errorStatus.downloadingStatus = Object.assign(
            {},
            this.state.downloadingStatus,
            {
              [invoiceId]: false,
            }
          );
        }
        this.setState(errorStatus);
      });
  };

  viewPdf = invoices =>
    this.setState({
      invoices,
      showInvoicesPDFModal: true,
    });

  goToOrder = (axBasketId, state, isInvoiced) => {
    const url = isInvoiced ? '/invoicedOrder' : '/order';
    this.props.history.push({
      pathname: `${url}/${axBasketId}`,
      state,
    });
  };

  closeNewOfferModal = () => {
    this.setState({showNewOfferModal: false});
    this.showReOrderWarningModalFunc();
  };

  render() {
    const {
      orderHistory,
      loading,
      errors,
      accounts,
      resetError,
      auth,
      relogin,
      basket,
      openStopEditModal,
      reAuthOrderPayment,
    } = this.props;
    const {
      showReOrderWarningModal,
      showEditOrderWarningModal,
      reOrdering,
      editing,
      showCancelOrderWarningModal,
      showInvoicesPDFModal,
      showNewOfferModal,
      downloadErrorIndex,
      downloadingStatus,
    } = this.state;
    if (!auth) {
      return <UnauthInfoPanel callback={relogin} loading={loading.auth} />;
    }

    const hasActiveOrders =
      orderHistory &&
      orderHistory._embedded &&
      orderHistory._embedded.activeOrders &&
      orderHistory._embedded.activeOrders.length > 0;

    const cancelledOrders =
      orderHistory &&
      orderHistory._embedded &&
      orderHistory._embedded.cancelledOrders;

    const hasCancelledOrders = cancelledOrders && cancelledOrders.length > 0;

    const hasInvoices =
      orderHistory &&
      orderHistory._embedded &&
      orderHistory._embedded.invoices &&
      orderHistory._embedded.invoices.length > 0;

    const renderReOrderWarningModal = showReOrderWarningModal && (
      <ReOrderBasketModal
        newBasketCallback={this.reOrder}
        addBasketCallback={this.reOrderAddBasket}
        showCancelButton={true}
        showOKButton={true}
        cancelCallBack={this.hideReOrderWarningModal}
        closeModalCallback={this.hideReOrderWarningModal}
        showModal={showReOrderWarningModal}
        isLoadingModal={this.state.reOrdering}
      />
    );
    const renderEditOrderWarningModal = showEditOrderWarningModal && (
      <EditOrderBasketModal
        newBasketCallback={this.editOrder}
        showCancelButton={true}
        showOKButton={true}
        cancelCallBack={this.hideEditOrderWarningModal}
        closeModalCallback={this.hideEditOrderWarningModal}
        showModal={showEditOrderWarningModal}
        isLoadingModal={this.state.editing}
        cutOffDateTime={this.state.cutOffDateTime}
      />
    );
    const renderLoadingModal = reOrdering && (
      <PopupModal
        modalName="Creating Edit Order"
        title={'Please wait'}
        content={'While we create your basket'}
        showModal={reOrdering}
        isError={false}
        isLoadingModal={true}
      />
    );
    const renderLoadingEditOrderModal = editing && (
      <PopupModal
        modalName="Loading Edit Order"
        title={'Please wait'}
        content={'While we create your basket'}
        showModal={editing}
        isError={false}
        isLoadingModal={true}
      />
    );

    const renderErrorReOrderModal = errors && errors.reOrderServerError && (
      <PopupModal
        modalName="ReOrder Error"
        title={'Error'}
        content={'Failed to reorder, please try again later'}
        showModal={errors.reOrderServerError}
        isError={true}
        callback={resetError}
      />
    );

    const renderErrorEditOrderModal = errors && errors.editOrderServerError && (
      <PopupModal
        modalName="Edit Order Error"
        title={'Error'}
        content={errors.editOrderServerError}
        showOKButton={true}
        showModal={errors.editOrderServerError}
        isError={true}
        callback={this.resetFailedToEdit}
      />
    );

    const renderErrorUpdateSettingsModal = errors && errors.updateSettings && (
      <PopupModal
        modalName="Update Order Error"
        title={'Error'}
        content={'Failed to update settings, please try again'}
        showModal={errors.updateSettings}
        isError={true}
        callback={resetError}
      />
    );

    const renderCancelOrderWarningModal = showCancelOrderWarningModal && (
      <PopupModal
        modalName="Cancel Order Warning"
        title={'Cancel Order'}
        content={'Are you sure you wish to cancel this order?'}
        showModal={showCancelOrderWarningModal}
        okButtonText={'Yes'}
        cancelButtonText={'No'}
        isLoadingModal={false}
        showOkButton={true}
        showCancelButton={true}
        cancelCallBack={this.hideCancelOrderWarningModal}
        callback={this.cancelOrder}
      />
    );
    const renderCancelOrderLoadingModal = loading &&
      loading.cancellingOrder && (
        <PopupModal
          modalName="Canceling Order Error"
          title={'Please wait'}
          content={'While we cancel your order'}
          showModal={loading.cancellingOrder}
          isError={false}
          isLoadingModal={true}
        />
      );

    const renderCancelOrderErrorModal = errors &&
      errors.cancelOrderServerError && (
        <PopupModal
          modalName="Cancel Order Error"
          title={'Error'}
          content={'Failed to cancel order, please try again later'}
          showModal={errors.cancelOrderServerError}
          isError={true}
          callback={resetError}
        />
      );

    const renderTrackingErrorModal = errors && errors.trackOrder && (
      <PopupModal
        modalName="Trace Order Error"
        title={'Sorry'}
        content={'No tracking information available for this order'}
        showModal={errors.trackOrder}
        isError={false}
        okButtonText={'Ok'}
        callback={resetError}
      />
    );

    const renderInvoicesPDFModal = showInvoicesPDFModal && (
      <InvoicesPDFModal
        invoices={this.state.invoices}
        showModal={showInvoicesPDFModal}
        callback={this.hideInvoicesModal}
        accessToken={auth && auth.access_token}
        downloadPdf={this.downloadPdf}
        loading={loading}
        downloadErrorIndex={downloadErrorIndex}
      />
    );

    const renderHistoryError = errors && errors.getOrderHistoryFailed && (
      <Error>Failed to get order history, please try again later</Error>
    );

    const renderActiveError = errors && errors.getOrderActiveFailed && (
      <Error>Failed to get active orders, please try again later</Error>
    );

    const renderCurrentOrders = hasActiveOrders && (
      <Row>
        {orderHistory._embedded.activeOrders.map(order => {
          return (
            <Order
              key={order && order.axBasketId}
              order={order}
              branchList={this.props.branchList}
              reOrder={this.showReOrderWarningModalFunc}
              cancelOrder={this.showCancelOrderWarningModal}
              editOrder={this.showEditOrderWarningModal}
              trackOrder={this.trackOrder}
              reAuthOrderPayment={reAuthOrderPayment}
              editing={
                basket && order && basket.axBasketId === order.axBasketId
              }
              openStopEditModal={openStopEditModal}
              goToOrder={this.goToOrder}
            />
          );
        })}
      </Row>
    );

    const renderActiveOrders = (
      <Container>
        {hasActiveOrders ? (
          <>
            {renderCurrentOrders}
            <Row>
              {orderHistory &&
                orderHistory.activeOrdersPage &&
                orderHistory.activeOrdersPage.totalPages > 1 && (
                  <PaginationWrapper>
                    <Pagination
                      pages={orderHistory.activeOrdersPage}
                      updateSearchParams={this.paginateActiveOrders}
                    />
                  </PaginationWrapper>
                )}
            </Row>
          </>
        ) : (
          <Row>
            <Error>You have no current orders</Error>
          </Row>
        )}
      </Container>
    );

    const renderInvoicesPagination = orderHistory &&
      orderHistory.invoicesPage &&
      orderHistory.invoicesPage.totalPages > 1 && (
        <Pagination
          pages={orderHistory.invoicesPage}
          updateSearchParams={this.paginateInvoices}
        />
      );

    const renderCancelledOrdersPagination = orderHistory &&
      orderHistory.cancelledOrdersPage &&
      orderHistory.cancelledOrdersPage.totalPages > 1 && (
        <Pagination
          pages={orderHistory.cancelledOrdersPage}
          updateSearchParams={this.paginateCancelledOrders}
        />
      );

    const invoices =
      orderHistory && orderHistory._embedded && orderHistory._embedded.invoices;

    const renderInvoices = (
      <Container $isLoading={loading && loading.orderHistory}>
        {hasInvoices ? (
          <Row>
            {invoices &&
              invoices.map(invoice => {
                if (invoice.salesId) {
                  return (
                    <Invoice
                      invoices={[invoice]}
                      key={invoice.salesId}
                      auth={auth}
                      reOrder={this.showReOrderWarningModalFunc}
                      viewPdf={this.viewPdf}
                      downloadPdf={this.downloadPdf}
                      goToOrder={this.goToOrder}
                      downloadingStatus={downloadingStatus}
                    />
                  );
                }
                if (invoice.axBasketId) {
                  return (
                    <Order
                      order={invoice}
                      key={invoice.axBasketId}
                      branchList={this.props.branchList}
                      reOrder={this.showReOrderWarningModalFunc}
                      isPreviousOrder={true}
                    />
                  );
                }
              })}
            <PaginationWrapper>{renderInvoicesPagination}</PaginationWrapper>
          </Row>
        ) : (
          <Row>
            <Error>You have no previous orders</Error>
          </Row>
        )}
      </Container>
    );

    const renderCancelledOrders = (
      <Container>
        {hasCancelledOrders ? (
          <Row>
            {cancelledOrders.map(order => (
              <Order
                key={order && order.axBasketId}
                order={order}
                branchList={this.props.branchList}
                reOrder={this.showReOrderWarningModalFunc}
                isPreviousOrder={true}
              />
            ))}
            <PaginationWrapper>
              {renderCancelledOrdersPagination}
            </PaginationWrapper>
          </Row>
        ) : (
          <Row>
            <Error>You have no cancelled orders</Error>
          </Row>
        )}
      </Container>
    );

    const renderNoOrdersFound = loading &&
      !loading.orderHistory &&
      !hasInvoices &&
      !hasActiveOrders &&
      !hasCancelledOrders &&
      errors &&
      !errors.getOrderHistoryFailed &&
      !errors.getOrderActiveFailed && (
        <Row>
          <Error>You have no previous orders</Error>
        </Row>
      );
    const renderLoading = loading &&
      (loading.orderHistory ||
        loading.activeOrders ||
        loading.cancelledOrders) && <Loading isLight={false} />;
    const renderSwitchAccount = accounts && accounts.length > 1 && (
      <OrderLinkButton onClick={this.openSwitchAccountModal}>
        Change Account
      </OrderLinkButton>
    );
    const renderNewOffersModal = showNewOfferModal && (
      <OffersModal
        showModal={true}
        callback={this.viewNewOffer}
        closeModalCallback={this.closeNewOfferModal}
      />
    );

    const renderTabs = (
      <TabsWrapper>
        <Tab
          $active={this.state.activeTab === TABS.ACTIVE}
          onClick={() => this.setActiveTab(TABS.ACTIVE)}
        >
          My current orders
        </Tab>
        <Tab
          $active={this.state.activeTab === TABS.PREVIOUS}
          onClick={() => this.setActiveTab(TABS.PREVIOUS)}
        >
          My previous orders
        </Tab>
        <Tab
          $active={this.state.activeTab === TABS.CANCELLED}
          onClick={() => this.setActiveTab(TABS.CANCELLED)}
        >
          Cancelled orders
        </Tab>
      </TabsWrapper>
    );
    const renderHistoryContent = ((loading && loading.orderHistory) ||
      hasActiveOrders ||
      hasInvoices ||
      hasCancelledOrders) && (
      <Content>
        {renderLoading}
        {renderTabs}
        {this.state.activeTab === TABS.ACTIVE && renderActiveOrders}
        {this.state.activeTab === TABS.PREVIOUS && renderInvoices}
        {this.state.activeTab === TABS.CANCELLED && renderCancelledOrders}
      </Content>
    );
    const c_account = auth && auth.c_account;
    const displayName = auth && auth.display_name;
    return (
      <Wrapper>
        {renderLoadingModal}
        {renderReOrderWarningModal}
        {renderNewOffersModal}
        {renderEditOrderWarningModal}
        {renderLoadingEditOrderModal}
        {renderErrorReOrderModal}
        {renderErrorEditOrderModal}
        {renderErrorUpdateSettingsModal}
        {renderCancelOrderWarningModal}
        {renderCancelOrderLoadingModal}
        {renderCancelOrderErrorModal}
        {renderTrackingErrorModal}
        {renderInvoicesPDFModal}
        <Head>
          <Title>My orders</Title>
          <ButtonWrapper>
            <UserInfo>
              Showing orders for account {c_account} - {displayName}
            </UserInfo>
            {renderSwitchAccount}
          </ButtonWrapper>
        </Head>
        {renderNoOrdersFound}
        {renderActiveError}
        {renderHistoryError}
        {renderHistoryContent}
        <ExtraWrapper>
          <OrderLinkButton to="/">Continue shopping</OrderLinkButton>
        </ExtraWrapper>
      </Wrapper>
    );
  }
}

const mapStateToProps = state => {
  return {
    settings: state.settings,
    branch: state.branch,
    accounts: state.accounts,
    branchList: state.branchList,
    basket: state.basket,
    orderHistory: state.orderHistory,
    order: state.order,
    loading: state.loading,
    auth: state.auth,
    errors: state.errors,
    nextRoute: state.nextRoute,
    modal: state.modal,
  };
};

const mapDispatchToProps = dispatch => ({
  reOrderBasket: bindActionCreators(reOrderBasket, dispatch),
  editOrder: bindActionCreators(editOrder, dispatch),
  getBasket: bindActionCreators(getBasket, dispatch),
  getOrderHistory: bindActionCreators(getOrderHistory, dispatch),
  getCanceledOrders: bindActionCreators(getCanceledOrders, dispatch),
  getActiveOrders: bindActionCreators(getActiveOrders, dispatch),
  cancelOrder: bindActionCreators(cancelOrder, dispatch),
  toggleSwitchAccountModal: bindActionCreators(toggleSwitchAccount, dispatch),
  setSetting: bindActionCreators(setSetting, dispatch),
  resetError: bindActionCreators(resetError, dispatch),
  trackOrder: bindActionCreators(trackOrder, dispatch),
  resetTrackOrder: bindActionCreators(resetTrackOrder, dispatch),
  reAuthOrderPayment: bindActionCreators(reAuthOrderPayment, dispatch),
  resetNextRoute: bindActionCreators(resetNextRoute, dispatch),
  setCurrentRoute: bindActionCreators(setCurrentRoute, dispatch),
  setPrevRoute: bindActionCreators(setPrevRoute, dispatch),
  startDownloading: bindActionCreators(startDownloadingPdf, dispatch),
  finishDownloading: bindActionCreators(finishDownloadingPdf, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrdersPage);
